/**
 * The words for a refused RudderStack account create or Reconnect (ACT2-1077). A leaf with no
 * imports, so the server builds each refusal's `problem` from it and the browser's refusal table
 * reads the same sentences instead of keeping its own copy.
 *
 * `PlatformCreateRefusedError`'s message is its reason code, so these are the only words a person
 * sees for one. `mapPlatformError` checks this table against `PlatformRefusalReason`, so a new reason
 * cannot ship without its copy.
 */
export const PLATFORM_REFUSAL_COPY = {
  family_not_enabled: {
    message: 'Lookout cannot create an account with this authentication method yet.',
    nextStep: 'Pick another authentication method.',
  },
  unsupported_type: {
    message: 'Lookout cannot manage an account for this warehouse type.',
    nextStep: 'Create the account in your workspace instead.',
  },
  invalid_identity: {
    message: 'These connection details do not identify a warehouse Lookout can create an account for.',
    nextStep: 'Check the host and database, then try again.',
  },
  connection_exists: {
    message: 'A connection for this warehouse already exists.',
    // Often another admin added it a moment ago, so the way forward is the existing one, not a delete.
    nextStep: 'Close this and open it from the warehouse list instead.',
  },
  use_setup_flow: {
    message: 'This authentication method needs the guided setup.',
    nextStep: 'Start again from Add account and follow the setup steps.',
  },
  external_id_conflict: {
    message: 'An account for this connection already exists.',
    nextStep: 'Open it in your workspace, or remove it and create this connection again.',
  },
  external_id_conflict_mismatch: {
    message: 'An account for these coordinates already exists with different settings.',
    nextStep: 'Remove it from your workspace, or enter the settings it was created with.',
  },
  reconnect_target_invalid: {
    message: 'These details do not match the connection you are reconnecting.',
    nextStep:
      'Check the host, database and any schema against the connection, and enter exactly what it was created with.',
  },
  no_retained_account: {
    message: 'Lookout has no account kept for these coordinates, so there is nothing to reconnect to.',
    nextStep: 'Create the account instead.',
  },
  retained_account_gone: {
    message: 'The account kept for this connection no longer exists, so it cannot be reconnected.',
    nextStep: 'Create the account again.',
  },
  retained_account_lookup_failed: {
    message: 'Lookout could not confirm whether the account kept for this connection still exists.',
    nextStep: 'Nothing was changed — try again in a moment.',
  },
  not_lookout_owned: {
    message: 'Lookout did not create this account for this connection, so Lookout will not change it.',
    nextStep: 'Manage it in your workspace.',
  },
  clickhouse_system_database: {
    message: "The system database is ClickHouse's own internal database, and it does not hold your data.",
    nextStep: 'Enter the name of the database that holds your data, then try again.',
  },
  clickhouse_host_ip_literal: {
    message: 'Lookout cannot connect to this host.',
    nextStep: "Use the server's host name or a public IPv4 address.",
  },
  clickhouse_host_private: {
    message: 'This host resolves to a private network address.',
    nextStep: 'Lookout connects only to public addresses, so use a host that has one.',
  },
  clickhouse_host_unresolved: {
    message: 'Lookout could not find this host name.',
    nextStep: 'Check the spelling of the host, then try again.',
  },
} as const satisfies Record<string, { message: string; nextStep: string }>;

export type PlatformRefusalCopyReason = keyof typeof PLATFORM_REFUSAL_COPY;
